// Higher-order component to inject hook result
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import {
  Box,
  Drawer,
  styled,
  Theme,
  useMediaQuery,
} from '@sbv-clients/ui-shared';

import { sideBarStyles } from './styles';

function HeightWatcher(props: {
  children: React.ReactNode;
  setHeight: (value: number | undefined) => void;
}) {
  const { height, ref } = useResizeDetector();
  useEffect(() => {
    props.setHeight(height);
  }, [height, props, props.setHeight]);
  return (
    <Box mb={1} flexShrink={0} ref={ref}>
      {props.children}
    </Box>
  );
}

function HeightDifference(props: { children: React.ReactNode; sm?: boolean }) {
  return (
    <Box
      flex={1}
      minHeight={0}
      {...(props.sm && { flexShrink: 1, minHeight: '80vh' })}
    >
      {React.Children.map(props.children, (child) => child)}
    </Box>
  );
}

const MainContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth',
})<{
  open?: boolean;
  drawerWidth: string;
}>(({ theme, open, drawerWidth }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: 0,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  }),
}));

const drawerContentFnDefault = (sm: boolean, m: boolean, lg: boolean) =>
  [m ? 360 : lg ? 440 : 390, 'px'].join('');
export const withFilters = <T,>(
  useFilterHook: () => T,
  Context: React.Context<T | null>,
  name: string,
  config?: {
    drawerOpenFn?: (c: T) => boolean;
    drawerContentFn?: (c: T) => React.ReactNode;
    drawerWidthFn?: (sm: boolean, m: boolean, lg: boolean) => string;
  }
) => {
  return {
    Page: ({ children }: { children: ReactNode }) => {
      const [, setHeight] = useState<number | undefined>(undefined);
      const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
      const m = useMediaQuery((theme: Theme) => theme.breakpoints.down(1400));
      const lg = useMediaQuery((theme: Theme) => theme.breakpoints.up(1600));
      const drawerWidth = config?.drawerWidthFn
        ? config.drawerWidthFn(sm, m, lg)
        : drawerContentFnDefault(sm, m, lg);

      const l = React.Children.count(children);
      const contextValue = useFilterHook();

      const open = config?.drawerOpenFn
        ? config?.drawerOpenFn(contextValue)
        : false;

      return (
        <Context.Provider value={contextValue}>
          <MainContent drawerWidth={drawerWidth} open={open}>
            <HeightWatcher setHeight={setHeight}>
              {React.Children.map(children, (child, index) => {
                if (index < l - 1) return <Box mb={2}>{child}</Box>;
              })}
            </HeightWatcher>
            <HeightDifference sm={sm}>
              {React.Children.map(children, (child, index) => {
                if (index === l - 1) return child;
              })}
            </HeightDifference>
          </MainContent>
          <Drawer
            PaperProps={{ sx: sideBarStyles }}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
              },
            }}
            variant="persistent"
            anchor="right"
            open={open}
          >
            {config?.drawerContentFn
              ? config?.drawerContentFn(contextValue)
              : null}
          </Drawer>
        </Context.Provider>
      );
    },

    useContextFn: () => {
      const context = useContext(Context);
      if (!context)
        throw new Error(`useContextFn must be used within ${name}Context`);
      return context;
    },
  };
};
